import { useQuery } from '@tanstack/react-query';
import type { ColumnDef, Row } from '@tanstack/react-table';
import { format } from 'date-fns';
import { ChevronDownIcon, ChevronRightIcon, DownloadIcon } from 'lucide-react';
import { overlay } from 'overlay-kit';
import {
  DATABASE_TYPE,
  type DatabaseItem,
  type UpdateHistoryRow,
  type UpdateManufacturingItem,
  type UpdatePackageItem,
} from '@/entities/database/model/types';
import { api } from '@/shared/api/api';
import { Badge, Button } from '@/shared/ui';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Separator } from '@/shared/ui/separator';
import { Spinner } from '@/shared/ui/spinner';
import { Text } from '@/shared/ui/text';
import { DataTable } from '../ui/data-table';

// URL 여부를 확인하는 헬퍼 함수
const isImageUrl = (value: string | null): boolean => {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

// 변경으로 취급하지 않는 필드 (서버 전용 관리·사용자 비노출, SDOCS-733).
// 개정번호(revision_no)는 서버에서만 관리되고 엑셀 업로드로 덮어쓰지 않으므로
// 변경 하이라이트 대상에서 제외한다. (백엔드 _is_data_changed 와 정합)
const NON_DIFF_FIELDS = new Set(['revision_no']);

// 변경된 필드인지 확인하는 헬퍼 함수
const isFieldChanged = (item: Record<string, unknown>, fieldName: string): boolean => {
  if (NON_DIFF_FIELDS.has(fieldName)) return false;
  const counterpart = item._counterpart as Record<string, unknown> | undefined;
  if (!counterpart || !item._updateType) return false;
  const currentVal = (item[fieldName] ?? '') as string;
  const otherVal = (counterpart[fieldName] ?? '') as string;
  return String(currentVal).trim() !== String(otherVal).trim();
};

// 변경된 필드에 적용할 CSS 클래스
const getChangedFieldClass = (item: Record<string, unknown>, fieldName: string): string => {
  if (!isFieldChanged(item, fieldName)) return '';
  return 'bg-yellow-50 border border-orange-300 rounded px-1';
};

// Update 타입에 따른 배경색 반환 헬퍼 함수
const getUpdateTypeBgClass = (
  item:
    | UpdatePackageItem
    | UpdateManufacturingItem
    | (UpdatePackageItem & { _updateType?: 'before' | 'after' })
    | (UpdateManufacturingItem & { _updateType?: 'before' | 'after' })
): string => {
  const updateType = (item as { _updateType?: 'before' | 'after' })._updateType;
  if (updateType === 'before') {
    return 'bg-red-100';
  }
  if (updateType === 'after') {
    return 'bg-green-100';
  }
  return '';
};

const createUpdateHistoryColumns = (
  dataType: keyof typeof DATABASE_TYPE
): ColumnDef<UpdateHistoryRow>[] => {
  const isPackage = dataType === DATABASE_TYPE.PACKAGE;

  return [
    {
      id: 'expander',
      header: '',
      cell: ({ row }) => {
        return row.getCanExpand() ? (
          <Button
            className="size-6"
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            size="icon"
            variant="ghost"
          >
            {row.getIsExpanded() ? (
              <ChevronDownIcon className="size-4" />
            ) : (
              <ChevronRightIcon className="size-4" />
            )}
          </Button>
        ) : null;
      },
    },
    {
      accessorKey: 'label',
      header: '구분',
      cell: ({ row }) => {
        if (row.depth === 0) {
          const historyRow = row.original;
          return (
            <Text typography="typo-text-xl-semibold">
              {historyRow.label} ({historyRow.count}건)
            </Text>
          );
        }

        const item = row.original as unknown as UpdatePackageItem | UpdateManufacturingItem;
        const updateType = (item as { _updateType?: 'before' | 'after' })._updateType;

        if (updateType === 'before') {
          return (
            <Text
              className="text-red-500"
              typography="typo-text-xl"
            >
              변경 전
            </Text>
          );
        }
        if (updateType === 'after') {
          return (
            <Text
              className="text-green-500"
              typography="typo-text-xl"
            >
              변경 후
            </Text>
          );
        }
        return null;
      },
    },
    {
      accessorKey: 'pk_no',
      header: 'PK',
      cell: ({ row }) => {
        if (row.depth === 0) return null;
        const changed = getChangedFieldClass(row.original as Record<string, unknown>, 'pk_no');
        return (
          <Text
            className={changed}
            typography="typo-text-xl"
          >
            {row.getValue('pk_no') || '-'}
          </Text>
        );
      },
    },
    ...(isPackage
      ? [
          {
            accessorKey: 'product_company_name',
            header: '제품명/회사명',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'product_company_name'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('product_company_name') || '-'}
                </Text>
              );
            },
          },
          {
            accessorKey: 'product_name',
            header: '제품명',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'product_name'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('product_name') || '-'}
                </Text>
              );
            },
          },
          {
            accessorKey: 'main_code',
            header: '대표코드',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'main_code'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('main_code') || '-'}
                </Text>
              );
            },
          },
          {
            accessorKey: 'product_code',
            header: '제품코드',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'product_code'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('product_code') || '-'}
                </Text>
              );
            },
          },
          {
            accessorKey: 'package_unit',
            header: '포장단위',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'package_unit'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('package_unit') || '-'}
                </Text>
              );
            },
          },
        ]
      : [
          {
            accessorKey: 'product_name',
            header: '제품명/회사명',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'product_name'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('product_name') || '-'}
                </Text>
              );
            },
          },
          {
            accessorKey: 'product_korean_name',
            header: '제품명',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'product_korean_name'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('product_korean_name') || '-'}
                </Text>
              );
            },
          },
          {
            accessorKey: 'product_english_name',
            header: '제품영문명',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'product_english_name'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('product_english_name') || '-'}
                </Text>
              );
            },
          },
          {
            accessorKey: 'company_name',
            header: '회사명',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'company_name'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('company_name') || '-'}
                </Text>
              );
            },
          },
          {
            accessorKey: 'main_code',
            header: '대표코드',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'main_code'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('main_code') || '-'}
                </Text>
              );
            },
          },
        ]),
    {
      accessorKey: 'document_no',
      header: '문서번호',
      cell: ({ row }) => {
        if (row.depth === 0) return null;
        const changed = getChangedFieldClass(
          row.original as Record<string, unknown>,
          'document_no'
        );
        return (
          <Text
            className={changed}
            typography="typo-text-xl"
          >
            {row.getValue('document_no') || '-'}
          </Text>
        );
      },
    },
    {
      accessorKey: 'revision_no',
      header: '개정번호',
      cell: ({ row }) => {
        if (row.depth === 0) return null;
        const changed = getChangedFieldClass(
          row.original as Record<string, unknown>,
          'revision_no'
        );
        return (
          <Text
            className={changed}
            typography="typo-text-xl"
          >
            {row.getValue('revision_no') || '-'}
          </Text>
        );
      },
    },
    {
      accessorKey: 'product_standard_no',
      header: '제품표준서 번호',
      cell: ({ row }) => {
        if (row.depth === 0) return null;
        const changed = getChangedFieldClass(
          row.original as Record<string, unknown>,
          'product_standard_no'
        );
        return (
          <Text
            className={changed}
            typography="typo-text-xl"
          >
            {row.getValue('product_standard_no') || '-'}
          </Text>
        );
      },
    },
    {
      id: 'pill_marking_group',
      header: '낱알 식별표시',
      meta: {
        className: 'text-center',
      },
      columns: [
        {
          accessorKey: 'pill_marking_one',
          header: '일면',
          meta: {
            className: 'text-center',
          },
          cell: ({ row }) => {
            if (row.depth === 0) return null;
            const pillMarkingOne = row.getValue('pill_marking_one') as string | null;
            if (isImageUrl(pillMarkingOne)) {
              return (
                <div className="relative w-20 h-20">
                  <img
                    alt="각인(앞)"
                    className="object-contain w-full h-full"
                    src={pillMarkingOne as string}
                  />
                </div>
              );
            }
            const changed = getChangedFieldClass(
              row.original as Record<string, unknown>,
              'pill_marking_one'
            );
            return (
              <Text
                className={`whitespace-pre-line text-center ${changed}`}
                typography="typo-text-xl"
              >
                {pillMarkingOne || '-'}
              </Text>
            );
          },
        },
        {
          accessorKey: 'pill_marking_two',
          header: '일면',
          meta: {
            className: 'text-center',
          },
          cell: ({ row }) => {
            if (row.depth === 0) return null;
            const pillMarkingTwo = row.getValue('pill_marking_two') as string | null;
            if (pillMarkingTwo && isImageUrl(pillMarkingTwo)) {
              return (
                <div className="relative w-20 h-20">
                  <img
                    alt="각인(뒤)"
                    className="object-contain w-full h-full"
                    src={pillMarkingTwo}
                  />
                </div>
              );
            }

            const changed = getChangedFieldClass(
              row.original as Record<string, unknown>,
              'pill_marking_two'
            );
            return (
              <Text
                className={`whitespace-pre-line text-center ${changed}`}
                typography="typo-text-xl"
              >
                {pillMarkingTwo || '-'}
              </Text>
            );
          },
        },
      ],
    },
    ...(isPackage
      ? [
          {
            accessorKey: 'barcode',
            header: '바코드',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'barcode'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('barcode')}
                </Text>
              );
            },
          },
          {
            accessorKey: 'standard_code',
            header: '표준코드',
            cell: ({ row }: { row: Row<UpdateHistoryRow> }) => {
              if (row.depth === 0) return null;
              const changed = getChangedFieldClass(
                row.original as Record<string, unknown>,
                'standard_code'
              );
              return (
                <Text
                  className={changed}
                  typography="typo-text-xl"
                >
                  {row.getValue('standard_code') || '-'}
                </Text>
              );
            },
          },
        ]
      : []),
  ];
};

interface UpdateHistoryDialogContentProps {
  readonly row: DatabaseItem;
  readonly queryKey: (masterId: string) => string[];
}

export function UpdateHistoryDialogContent({ row, queryKey }: UpdateHistoryDialogContentProps) {
  const needsFetch =
    typeof row.update_history?.delete === 'number' ||
    typeof row.update_history?.insert === 'number' ||
    typeof row.update_history?.update === 'number';

  const {
    data: detailHistory,
    isPending,
    error,
  } = useQuery({
    queryKey: queryKey(row.master_data_id),
    queryFn: async () => {
      const response = await api.get<{
        insert_items: (UpdatePackageItem | UpdateManufacturingItem)[];
        delete_items: (UpdatePackageItem | UpdateManufacturingItem)[];
        update_items: {
          before: UpdatePackageItem | UpdateManufacturingItem;
          after: UpdatePackageItem | UpdateManufacturingItem;
        }[];
      }>(`master-data/${row.master_data_id}`);
      // 백엔드 필드명을 프론트엔드 형식으로 변환
      return {
        insert: response.insert_items,
        delete: response.delete_items,
        update: response.update_items,
      };
    },
    enabled: needsFetch,
    retry: false,
  });

  if (error) {
    return (
      <DialogContent className="!max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>오류</DialogTitle>
          <DialogDescription>
            {error instanceof Error
              ? error.message
              : '업데이트 내역을 불러오는 중 오류가 발생했습니다.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">닫기</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    );
  }

  const history = needsFetch ? detailHistory : row.update_history;

  // 타입 체크를 한 번만 수행하고 결과 저장
  const deleteItems: (UpdatePackageItem | UpdateManufacturingItem)[] =
    history && Array.isArray(history.delete) ? history.delete : [];
  const insertItems: (UpdatePackageItem | UpdateManufacturingItem)[] =
    history && Array.isArray(history.insert) ? history.insert : [];
  const updateItems: {
    before: UpdatePackageItem | UpdateManufacturingItem;
    after: UpdatePackageItem | UpdateManufacturingItem;
  }[] = history && Array.isArray(history.update) ? history.update : [];

  const deleteCount = deleteItems.length;
  const insertCount = insertItems.length;
  const updateCount = updateItems.length;
  const totalCount = deleteCount + insertCount + updateCount;

  // UpdateList를 계층 구조 데이터로 변환
  const historyData: UpdateHistoryRow[] = [
    deleteCount > 0 && {
      type: 'delete' as const,
      label: '삭제',
      count: deleteCount,
      subRows: deleteItems,
    },
    insertCount > 0 && {
      type: 'insert' as const,
      label: '추가',
      count: insertCount,
      subRows: insertItems,
    },
    updateCount > 0 && {
      type: 'update' as const,
      label: '수정',
      count: updateCount,
      subRows: updateItems.flatMap((item) => [
        { ...item.before, _updateType: 'before' as const, _counterpart: item.after },
        { ...item.after, _updateType: 'after' as const, _counterpart: item.before },
      ]),
    },
  ].filter(Boolean) as UpdateHistoryRow[];

  return (
    <DialogContent className="!max-w-[90vw]">
      <DialogHeader>
        <DialogTitle>업데이트 내역</DialogTitle>
        <DialogDescription className="sr-only">업데이트 내역을 확인해주세요.</DialogDescription>
      </DialogHeader>
      {isPending && needsFetch ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4 min-h-[400px]">
          <Spinner size="xl" />
          <Text
            className="text-muted-foreground"
            typography="typo-text-xl-semibold"
          >
            업데이트 내역을 불러오는 중...
          </Text>
        </div>
      ) : (
        <div className="flex flex-col gap-2 w-full overflow-hidden">
          <header className="w-full flex justify-between items-center border rounded py-4 px-6 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Badge>
                {row.master_data_type === DATABASE_TYPE.MANUFACTURE ? '제조지시' : '포장지시'}
              </Badge>
              <Text typography="typo-text-xl">
                {row.master_data_name} - {row.created_by_name}
              </Text>
            </div>

            <div className="flex items-center gap-2">
              <Text
                className="flex items-center"
                tag="span"
                typography="typo-text-xl-semibold"
              >
                총&nbsp;
                <Text
                  className="text-primary"
                  typography="typo-text-xl-semibold"
                >
                  {totalCount}
                </Text>
                건
              </Text>
              <Separator
                className="bg-border !h-4"
                orientation="vertical"
              />
              <Text typography="typo-text-xl-semibold">
                {format(row.created_at, 'yyyy-MM-dd HH:mm')}
              </Text>
              <Separator orientation="vertical" />
            </div>
          </header>
          <div className="w-full max-h-[500px]">
            {history ? (
              <DataTable
                columns={createUpdateHistoryColumns(row.master_data_type)}
                data={historyData}
                enablePagination={false}
                getRowCanExpand={(row) => {
                  const historyRow = row;
                  return !!(historyRow.subRows && historyRow.subRows.length > 0);
                }}
                getRowClassName={(row) => {
                  const item = row as
                    | UpdateHistoryRow
                    | UpdatePackageItem
                    | UpdateManufacturingItem;
                  return getUpdateTypeBgClass(item as UpdatePackageItem | UpdateManufacturingItem);
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <Text
                  className="text-muted-foreground"
                  typography="typo-text-xl"
                >
                  업데이트 내역이 없습니다.
                </Text>
              </div>
            )}
          </div>
        </div>
      )}
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="secondary">닫기</Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
}

export const createDatabaseColumns = (
  queryKey: (masterId: string) => string[]
): ColumnDef<DatabaseItem>[] => [
  {
    accessorKey: 'created_at',
    header: '날짜',
    cell: ({ row }) => (
      <Text typography="typo-text-xl">
        {format(new Date(row.getValue('created_at')), 'yyyy-MM-dd HH:mm')}
      </Text>
    ),
  },
  {
    accessorKey: 'master_data_name',
    header: '파일명',
  },
  {
    accessorKey: 'master_data_type',
    header: '유형',
    cell: ({ row }) => {
      const dataType = row.getValue('master_data_type');
      return (
        <Text typography="typo-text-xl">
          {dataType === DATABASE_TYPE.MANUFACTURE ? '제조지시' : '포장지시'}
        </Text>
      );
    },
  },
  {
    accessorKey: 'update_history',
    header: '업데이트',
    cell: ({ row }) => {
      const updateList = row.original.update_history;

      const getCount = (value: number | unknown[] | undefined): number => {
        if (typeof value === 'number') return value;
        if (Array.isArray(value)) return value.length;
        return 0;
      };

      const deleteCount = getCount(updateList?.delete);
      const insertCount = getCount(updateList?.insert);
      const updateCount = getCount(updateList?.update);

      const mergedText = [
        deleteCount > 0 && `삭제 ${deleteCount}건`,
        insertCount > 0 && `추가 ${insertCount}건`,
        updateCount > 0 && `수정 ${updateCount}건`,
      ]
        .filter(Boolean)
        .join(', ');

      const handleOpenUpdateList = (row: DatabaseItem) => {
        if (!row.update_history) return;

        window.history.pushState(null, '', `/database/${row.master_data_id}`);
        overlay.open(({ isOpen, close }) => (
          <Dialog
            onOpenChange={() => {
              close();
              window.history.pushState(null, '', '/database');
            }}
            open={isOpen}
          >
            <UpdateHistoryDialogContent
              queryKey={queryKey}
              row={row}
            />
          </Dialog>
        ));
      };

      return (
        <Text
          className="text-blue-500 hover:underline cursor-pointer"
          onClick={() => handleOpenUpdateList(row.original)}
          typography="typo-text-xl"
        >
          {mergedText}
        </Text>
      );
    },
  },
  {
    accessorKey: 'created_by_name',
    header: '작업자',
  },
  {
    accessorKey: 'master_data_document_url',
    header: '다운로드',
    cell: ({ row }) => {
      return (
        <Button
          className="size-6"
          onClick={() => {
            window.open(row.original.master_data_document_url, '_blank');
          }}
          size="icon"
          variant="ghost"
        >
          <DownloadIcon className="size-4" />
        </Button>
      );
    },
  },
];
